import { html, nothing, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, state } from 'lit/decorators.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';

import { PopApiBaseComponent } from '../../base/pop-api-base-component.js';
import { Constants, TABS } from './purchases-and-sales.constants.js';
import type { PurchasesAndSalesTab } from './purchases-and-sales.constants.js';
import type {
  PurchasesAndSalesContentObject,
  PurchasesAndSalesItem,
  PurchasesAndSalesItemWithoutRank,
} from './purchases-and-sales.model.js';
import {
  dateShort,
  dateStringSplit,
  dateToMMDDYYYY,
} from '../../common/utils/formatDate';
import style from './purchases-and-sales.scss?inline';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import {
  isItemWithRank,
  removeRankFromItem,
} from './purchases-and-sales.utils.js';
import { PurchasesAndSalesFragments } from '../../common/fragments/purchases-and-sales.fragments.js';

/**
 * @tag pop-purchases-and-sales-component
 * @property {ContributorsDetractors} [componentObject] Purchases and Sales content object
 * @attribute { PurchasesAndSalesContentObject } [data-component-object] Purchases and Sales content object
 */
@customElement(Constants.TAG_NAME)
export class PopPurchasesAndSalesComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * Purchases and Sales content object
   */
  componentObject?: PurchasesAndSalesContentObject;

  /**
   * @internal
   */
  fragment = PurchasesAndSalesFragments.purchasesAndSalesFragment;

  @state()
  selectedAsOfDate: string | undefined = '';

  /**
   * @internal
   * Storing this as state is being used to enable the workaround we are using
   * to ensure the chart renders consistently when switching between tabs.
   */
  @state()
  selectedTab: PurchasesAndSalesTab = TABS.PURCHASES;

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = _response?.purchaseSalesExhibit;
  }

  private renderTopSection() {
    return html`
      <h3
        class="purchases-and-sales-heading b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}<span
          data-disclaimer-code="${Constants.GLOBAL_DISCLAIMER_CODE}"
        ></span>
      </h3>
    `;
  }

  private renderControlSection() {
    return html`
      <div
        class="purchases-and-sales-controls b-margin-block-start--micro--jumbo"
      >
        ${this.renderAsOfDate()}
      </div>
    `;
  }

  private renderAsOfDate() {
    const availableDates = this.componentObject?.availablePriorDates;

    return html`
      ${availableDates && availableDates.length === 1
        ? html` <p
            data-testid="single-as-of-date"
            class="b-typography-sans-serif--caption-loose--300
            b-font-weight-light"
          >
            ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
            ${dateShort(availableDates[0])}
          </p>`
        : nothing}
      ${availableDates && availableDates.length > 1
        ? html`<beacon-control>
            <label slot="label" for="${Constants.DATA_AS_OF_DROPDOWN_ID}"
              >${Constants.DATA_AS_OF_DROPDOWN_LABEL}</label
            >
            <select
              id="${Constants.DATA_AS_OF_DROPDOWN_ID}"
              @change="${this.asOfDateChangeHandler}"
            >
              ${availableDates.map(
                (date) =>
                  html`<option
                    value="${date}"
                    ?selected=${date === this.componentObject?.asOfDate}
                  >
                    ${dateToMMDDYYYY(date)}
                  </option>`,
              )}
            </select>
          </beacon-control> `
        : nothing}
    `;
  }

  private asOfDateChangeHandler(
    event: Event & { target?: { value?: string } },
  ) {
    if (event.target?.value) {
      this.selectedAsOfDate = dateStringSplit(event.target?.value);
      this.refreshData();
    }
  }

  private refreshData(): void {
    this.callApiService({
      historicalDates: { purchaseSales: [this.selectedAsOfDate] },
    });
  }

  private renderTabSection() {
    return html`
      <beacon-tabs variant="secondary" tab-type="standard">
        <beacon-tab
          slot="tab-children"
          label="${Constants.PURCHASES_TAB_LABEL}"
          @click="${() => {
            this.selectedTab = TABS.PURCHASES;
          }}"
        ></beacon-tab>
        <beacon-tab
          slot="tab-children"
          label="${Constants.SALES_TAB_LABEL}"
          @click="${() => {
            this.selectedTab = TABS.SALES;
          }}"
        ></beacon-tab>
        <beacon-tab-panel slot="tab-panel">
          <!--
           The condition checked here is being used as a workaround to ensure
           that the chart renders correctly.
          -->
          ${this.selectedTab === TABS.PURCHASES
            ? this.renderPurchasesTab()
            : html``}
        </beacon-tab-panel>
        <beacon-tab-panel slot="tab-panel">
          <!--
           The condition checked here is being used as a workaround to ensure
           that the chart renders correctly.
          -->
          ${this.selectedTab === TABS.SALES ? this.renderSalesTab() : html``}
        </beacon-tab-panel>
      </beacon-tabs>
    `;
  }

  private renderPurchasesTab() {
    const itemList = this.componentObject?.purchasesList;
    if (!itemList || itemList.length === 0) {
      return html`<pop-no-data-available-component
        .message="${Constants.PURCHASES_UNAVAILABLE_MESSAGE}"
      ></pop-no-data-available-component>`;
    }

    return html`${this.renderChartSection(
      this.componentObject?.purchasesList || [],
    )}
    ${this.renderTableSection(itemList)}`;
  }

  private renderSalesTab() {
    const itemList = this.componentObject?.salesList;
    if (!itemList || itemList.length === 0) {
      return html`<pop-no-data-available-component
        .message="${Constants.SALES_UNAVAILABLE_MESSAGE}"
      ></pop-no-data-available-component>`;
    }

    return html`${this.renderChartSection(
      this.componentObject?.salesList || [],
    )}
    ${this.renderTableSection(itemList)}`;
  }

  private renderChartSection(list: PurchasesAndSalesItem[]) {
    const sortedList = this.getItemsSortedByRank(list);

    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <pop-purchases-and-sales-chart-component
          data-component-object="${JSON.stringify({
            itemList: sortedList,
            maxYAxisValue: this.getChartMaxPointValue(),
          })}"
        >
        </pop-purchases-and-sales-chart-component>
      </div>
    `;
  }

  /**
   * @returns the largest value out of all the purchases and sales percent of portfolio,
   * both current and previous
   */
  private getChartMaxPointValue() {
    const purchases = this.componentObject?.purchasesList || [];
    const sales = this.componentObject?.salesList || [];

    const combinedList = [...purchases, ...sales];

    return Math.max(
      ...combinedList.map((item) =>
        Math.max(
          item.percentTotalNetAssets || 0,
          item.previousQuarterPercentTotalNetAssets || 0,
        ),
      ),
    );
  }

  private renderTableSection(itemList: PurchasesAndSalesItem[]) {
    const sortedList = this.getItemsSortedByRank(itemList);

    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <pop-purchases-and-sales-table-component
          data-component-object="${JSON.stringify({ itemList: sortedList })}"
        >
        </pop-purchases-and-sales-table-component>
      </div>
    `;
  }

  /**
   * Takes the list of items (purchases or sales) and returns them ordered by rank
   * with the rank property removed, as it won't be used after this point.
   * Items without a rank are removed.
   * @param itemList
   * @returns the items ordered by rank (with un-ranked items removed)
   */
  private getItemsSortedByRank(
    itemList: PurchasesAndSalesItem[],
  ): PurchasesAndSalesItemWithoutRank[] {
    return itemList
      .filter(isItemWithRank)
      .sort((a, b) => a.rank - b.rank)
      .map(removeRankFromItem);
  }

  private renderDisclaimerSection() {
    const disclaimers = this.componentObject?.disclaimers;
    if (!disclaimers || disclaimers.length === 0) {
      return html``;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  render() {
    return this.renderComponent(
      html`<section class="b-container">
        ${this.renderTopSection()} ${this.renderControlSection()}
        ${this.renderTabSection()} ${this.renderDisclaimerSection()}
      </section>`,
    );
  }
}
